import { withAuthenticationRequired } from "@auth0/auth0-react";
import { store } from "../store";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Paper,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Download,
  Store,
  CalendarMonth,
  DateRange,
  CalendarToday,
  Description,
} from "@mui/icons-material";
import fetchData from "../API/Fetch";

function Export() {
  const { t } = useTranslation("common");

  const establishments = useSelector((state) => state.establishments);

  // États pour les filtres
  const [selectedEstablishment, setSelectedEstablishment] = useState(
    establishments?.[0]?.id || ""
  );
  const [selectedZone, setSelectedZone] = useState("all");
  const [availableZones, setAvailableZones] = useState([]);

  // Type de document : "conso" (CSV consommations, historique) ou "index" (relevé d'index)
  const [docType, setDocType] = useState("conso");

  // États pour la période
  const [periodType, setPeriodType] = useState("month"); // month, range, year
  const [selectedMonth, setSelectedMonth] = useState(
    String(new Date().getMonth() + 1).padStart(2, "0")
  );
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Pour le mode période personnalisée
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Charger les zones quand l'établissement change
  useEffect(() => {
    const loadZones = async () => {
      if (selectedEstablishment) {
        try {
          const zonesData = await fetchData(`/zones/establishment/${selectedEstablishment}`);
          setAvailableZones(zonesData || []);
        } catch (error) {
          setAvailableZones([]);
        }
      } else {
        setAvailableZones([]);
      }
      setSelectedZone("all");
    };
    loadZones();
  }, [selectedEstablishment]);

  // Mois
  const months = [
    { value: "01", label: "Janvier", short: "Jan" },
    { value: "02", label: "Février", short: "Fév" },
    { value: "03", label: "Mars", short: "Mar" },
    { value: "04", label: "Avril", short: "Avr" },
    { value: "05", label: "Mai", short: "Mai" },
    { value: "06", label: "Juin", short: "Juin" },
    { value: "07", label: "Juillet", short: "Juil" },
    { value: "08", label: "Août", short: "Août" },
    { value: "09", label: "Septembre", short: "Sep" },
    { value: "10", label: "Octobre", short: "Oct" },
    { value: "11", label: "Novembre", short: "Nov" },
    { value: "12", label: "Décembre", short: "Déc" },
  ];

  // Années disponibles
  const years = Array.from(
    { length: new Date().getFullYear() - 2023 },
    (_, i) => 2024 + i
  );

  useEffect(() => {
    store.dispatch({
      type: "pageTitle/update",
      payload: t("common.exporter"),
    });
    store.dispatch({ type: "pageActions/update", payload: [] });
  }, [t]);

  // Sélectionner un mois
  const handleMonthSelect = (monthValue) => {
    setPeriodType("month");
    setSelectedMonth(monthValue);
  };

  // Sélectionner année complète
  const handleYearSelect = () => {
    setPeriodType("year");
  };

  // Télécharger le CSV
  const downloadCsv = async () => {
    let endpoint = "";
    let filename = "";

    const establishment = establishments.find(
      (e) => parseInt(e.id) === parseInt(selectedEstablishment)
    );

    if (!establishment) return;

    if (docType === "index") {
      // Relevé d'index : un seul endpoint par plage de dates, quel que soit le mode de période
      let rangeStart = "";
      let rangeEnd = "";
      if (periodType === "month") {
        const lastDay = new Date(selectedYear, parseInt(selectedMonth), 0).getDate();
        rangeStart = `${selectedYear}-${selectedMonth}-01`;
        rangeEnd = `${selectedYear}-${selectedMonth}-${String(lastDay).padStart(2, "0")}`;
        filename = `${establishment.name}_${selectedYear}_${selectedMonth}_releve_index.csv`;
      } else if (periodType === "year") {
        rangeStart = `${selectedYear}-01-01`;
        rangeEnd = `${selectedYear}-12-31`;
        filename = `${establishment.name}_${selectedYear}_releve_index.csv`;
      } else if (periodType === "range" && startDate && endDate) {
        rangeStart = startDate;
        rangeEnd = endDate;
        filename = `${establishment.name}_${startDate}_au_${endDate}_releve_index.csv`;
      } else {
        return;
      }
      endpoint = `/establishments/report-index/${selectedEstablishment}/range?start_date=${rangeStart}&end_date=${rangeEnd}`;
    } else if (periodType === "month") {
      // Format: /report/{id}/{mmyyyy}
      endpoint = `/establishments/report/${selectedEstablishment}/${selectedMonth}${selectedYear}`;
      filename = `${establishment.name}_${selectedYear}_${selectedMonth}.csv`;
    } else if (periodType === "year") {
      // Année complète: /report/{id}/range?start_date=YYYY-01-01&end_date=YYYY-12-31
      endpoint = `/establishments/report/${selectedEstablishment}/range?start_date=${selectedYear}-01-01&end_date=${selectedYear}-12-31`;
      filename = `${establishment.name}_${selectedYear}_annee_complete.csv`;
    } else if (periodType === "range" && startDate && endDate) {
      // Période personnalisée: /report/{id}/range?start_date=X&end_date=Y
      endpoint = `/establishments/report/${selectedEstablishment}/range?start_date=${startDate}&end_date=${endDate}`;
      filename = `${establishment.name}_${startDate}_au_${endDate}.csv`;
    } else {
      return;
    }

    // Ajouter le filtre zone si sélectionné
    if (selectedZone !== "all") {
      endpoint += endpoint.includes("?") ? `&zone_id=${selectedZone}` : `?zone_id=${selectedZone}`;
      const zone = availableZones.find(z => z.id === selectedZone);
      if (zone) {
        filename = filename.replace(".csv", `_${zone.name}.csv`);
      }
    }

    try {
      const data = await fetchData(endpoint);

      if (!data) {
        return;
      }

      const csv = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(csv);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Erreur silencieuse
    }
  };

  // Générer le résumé de l'export
  const getExportSummary = () => {
    const what = docType === "index" ? "Relevé d'index" : "Consommations";
    if (periodType === "month") {
      const monthLabel = months.find(m => m.value === selectedMonth)?.label;
      return `${what} • mois de ${monthLabel} ${selectedYear}`;
    } else if (periodType === "year") {
      return `${what} • année ${selectedYear} complète`;
    } else if (periodType === "range" && startDate && endDate) {
      return `${what} • du ${new Date(startDate).toLocaleDateString('fr-FR')} au ${new Date(endDate).toLocaleDateString('fr-FR')}`;
    }
    return "Sélectionnez une période";
  };

  // Vérifier si l'export est possible
  const canExport = () => {
    if (periodType === "month" || periodType === "year") return true;
    if (periodType === "range" && startDate && endDate) return true;
    return false;
  };

  // Style commun pour les sections
  const sectionStyle = {
    p: 3,
    mb: 2,
    borderRadius: 2,
  };

  return (
    <Grid container spacing={2}>
      {/* Section Établissement & Zone */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: "4px solid #6b9dad" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Store sx={{ color: "#6b9dad" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Établissement & Zone
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Établissement</InputLabel>
                <Select
                  value={selectedEstablishment}
                  label="Établissement"
                  onChange={(e) => setSelectedEstablishment(e.target.value)}
                >
                  {establishments.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Zone</InputLabel>
                <Select
                  value={selectedZone}
                  label="Zone"
                  onChange={(e) => setSelectedZone(e.target.value)}
                  disabled={availableZones.length === 0}
                >
                  <MenuItem value="all">Toutes les zones</MenuItem>
                  {availableZones.map((zone) => (
                    <MenuItem key={zone.id} value={zone.id}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: zone.color || "#c4a574",
                          }}
                        />
                        {zone.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Section Type de document */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: "4px solid #9b7fb8" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Description sx={{ color: "#9b7fb8" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Type de document
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {[
              { value: "conso", label: "Consommations", hint: "Eau, électricité et coûts par logement" },
              { value: "index", label: "Relevé d'index", hint: "Index début et fin de période de chaque compteur, avec dates" },
            ].map((opt) => (
              <Box
                key={opt.value}
                onClick={() => setDocType(opt.value)}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "16px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  backgroundColor: docType === opt.value ? "#9b7fb8" : "transparent",
                  border: docType === opt.value ? "none" : "1px solid rgba(255,255,255,0.2)",
                  "&:hover": {
                    backgroundColor: docType === opt.value ? "#8a6fa7" : "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: docType === opt.value ? 600 : 400,
                    color: docType === opt.value ? "white" : "rgba(255,255,255,0.7)",
                  }}
                >
                  {opt.label}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: docType === opt.value ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.5)" }}>
                  {opt.hint}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Grid>

      {/* Section Période */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: "4px solid #c4a574" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <CalendarMonth sx={{ color: "#c4a574" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Période
            </Typography>
          </Box>

          {/* Sélection de mois */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarToday sx={{ fontSize: 18 }} />
              Sélectionner un mois
            </Typography>

            {/* Sélecteur d'année */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Année</InputLabel>
                <Select
                  value={selectedYear}
                  label="Année"
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton Année complète */}
              <Box
                onClick={handleYearSelect}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "20px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  backgroundColor: periodType === "year" ? "#78a882" : "transparent",
                  border: periodType === "year" ? "none" : "1px solid rgba(255,255,255,0.2)",
                  "&:hover": {
                    backgroundColor: periodType === "year" ? "#6a9a74" : "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: periodType === "year" ? 600 : 400,
                    color: periodType === "year" ? "white" : "rgba(255,255,255,0.7)",
                  }}
                >
                  Année complète
                </Typography>
              </Box>
            </Box>

            {/* Pills des mois */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {months.map((m) => (
                <Box
                  key={m.value}
                  onClick={() => handleMonthSelect(m.value)}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: "16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: (periodType === "month" && selectedMonth === m.value) ? "#c4a574" : "transparent",
                    border: (periodType === "month" && selectedMonth === m.value) ? "none" : "1px solid rgba(255,255,255,0.2)",
                    "&:hover": {
                      backgroundColor: (periodType === "month" && selectedMonth === m.value) ? "#b39464" : "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: (periodType === "month" && selectedMonth === m.value) ? 600 : 400,
                      color: (periodType === "month" && selectedMonth === m.value) ? "white" : "rgba(255,255,255,0.7)",
                    }}
                  >
                    {m.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Période personnalisée */}
          <Box>
            <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <DateRange sx={{ fontSize: 18 }} />
              Période personnalisée
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date de début"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPeriodType("range");
                  }}
                  onClick={(e) => e.target.showPicker?.()}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    cursor: "pointer",
                    "& input": { cursor: "pointer" }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date de fin"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPeriodType("range");
                  }}
                  onClick={(e) => e.target.showPicker?.()}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    cursor: "pointer",
                    "& input": { cursor: "pointer" }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      {/* Bouton Export */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: "4px solid #78a882" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 1 }}>
                <Download sx={{ color: "#78a882" }} />
                Exporter les données
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {getExportSummary()}
                {selectedZone !== "all" && ` • Zone: ${availableZones.find(z => z.id === selectedZone)?.name}`}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<Download />}
              onClick={downloadCsv}
              disabled={!canExport()}
              sx={{
                backgroundColor: "#78a882",
                px: 4,
                py: 1.5,
                "&:hover": { backgroundColor: "#6a9a74" },
              }}
            >
              Télécharger CSV
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(Export);
